import React, { Component, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Alert, Button, Box, Typography, Stack } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import UpdateIcon from "@mui/icons-material/Update";
import { isChunkLoadError } from "../../utils/lazyWithRetry";
import { captureException } from "../../config/sentry";

interface RouteErrorBoundaryInnerState {
  hasError: boolean;
  error?: Error;
}

interface RouteErrorBoundaryInnerProps {
  children: ReactNode;
}

/**
 * RouteErrorBoundary — Route-level error boundary inside MainLayout.
 *
 * Catches render errors from lazy-loaded route chunks while keeping
 * the sidebar and header visible. For chunk-load failures (stale
 * deployment), shows a "New version available" prompt. For other
 * errors, shows a generic retry screen.
 *
 * Uses location.key as React key so the boundary resets on navigation.
 *
 * @see AppErrorBoundary for the outer safety net above MainLayout
 */
class RouteErrorBoundaryInner extends Component<
  RouteErrorBoundaryInnerProps,
  RouteErrorBoundaryInnerState
> {
  constructor(props: RouteErrorBoundaryInnerProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryInnerState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[RouteErrorBoundary] Uncaught error:", error, errorInfo);

    captureException(error, {
      componentStack: errorInfo.componentStack,
      boundary: "RouteErrorBoundary",
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const error = this.state.error;
    const isChunkError = error ? isChunkLoadError(error) : false;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          p: 4,
          textAlign: "center",
        }}
      >
        {isChunkError ? (
          <UpdateIcon sx={{ fontSize: 64, color: "info.main", mb: 2 }} />
        ) : (
          <ErrorOutlineIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
        )}

        <Typography variant="h5" gutterBottom fontWeight={600}>
          {isChunkError ? "New Version Available" : "Page Failed to Load"}
        </Typography>

        <Typography
          variant="body1"
          sx={{ color: "var(--ink-3)", mb: 3, maxWidth: 500 }}
        >
          {isChunkError
            ? "A new version of the application has been deployed. Please reload the page to get the latest version."
            : "An unexpected error occurred while loading this page. You can try again or navigate to a different page."}
        </Typography>

        {!isChunkError && error && (
          <Alert severity="error" sx={{ mb: 3, maxWidth: 500 }}>
            {error.message || "An unknown error occurred"}
          </Alert>
        )}

        <Stack direction="row" spacing={2}>
          {isChunkError ? (
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={this.handleReload}
              size="large"
            >
              Reload Page
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                onClick={this.handleReset}
                size="large"
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={this.handleReload}
                size="large"
              >
                Reload Page
              </Button>
            </>
          )}
        </Stack>

        {import.meta.env.DEV && error && (
          <Box
            sx={{
              mt: 4,
              p: 2,
              backgroundColor: "var(--bg-sunken)",
              borderRadius: 1,
              maxWidth: 800,
              width: "100%",
              textAlign: "left",
            }}
          >
            <Typography variant="caption" fontWeight={600} gutterBottom>
              Developer Info:
            </Typography>
            <Typography
              variant="caption"
              component="pre"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {error.stack || error.message}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
}

/**
 * Wrapper that resets the error boundary when the user navigates
 * to a different route (pathname change only).
 *
 * Keying on location.pathname rather than location.key is intentional:
 * location.key changes on every navigation entry — including URL
 * search-param updates like ?q=foo → ?q=bar — which would unmount and
 * remount the entire page tree on every debounce flush or filter change,
 * destroying component state and stealing keyboard focus.
 * Pathname-based keying resets the boundary only on genuine route
 * transitions, which is the correct reset semantics for an error boundary.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();

  return (
    <RouteErrorBoundaryInner key={location.pathname}>
      {children}
    </RouteErrorBoundaryInner>
  );
}
